import { getSubjectLabels } from '@/lib/settings';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardSection } from '../../ui/wizard-layout';
import { type PreviewEvent, type TeacherTimetableSelectEvent } from '../../schedule/TeacherTimetable';
import WizardScheduleGrid from '../../schedule/WizardScheduleGrid';
import { cn } from '../../../lib/utils';
import { ChevronDown } from 'lucide-react';
import type { FachItem, KursItem } from '../../../lib/courses';
import type { LehrerOption } from '../../../lib/unterrichtseinheiten';
import type { TeacherAvailabilitySlot } from '../../../lib/personen';
import type { SelectedSlot, Step1Section } from './types';

type StepLessonScheduleProps = Readonly<{
  step1ActiveSection: Step1Section;
  step1UnterrichtCollapsed: boolean;
  step1TerminplanCollapsed: boolean;
  selectedSubjectName: string;
  teacherSummaryName: string;
  selectedCourseId?: number;
  selectedCourse: KursItem | null;
  fachOptions: FachItem[];
  fachId?: number;
  teacherOptions: LehrerOption[];
  teacherId?: number;
  lockTeacher: boolean;
  teacherAvailability: TeacherAvailabilitySlot[];
  selectedSlot: SelectedSlot;
  previewEvent: PreviewEvent | null;
  timetableDayOfWeek: number;
  timetableReferenceDate: Date;
  canShowTerminplan: boolean;
  missingScheduleSelectionText: string;
  teacherAvailabilityHint: string;
  showOutsideAvailabilityWarning: boolean;
  onActivateSection: (section: Step1Section) => void;
  onSelectFach: (fachId: number) => void;
  onSelectTeacher: (teacherId: number) => void;
  onClearCourse: () => void;
  onSelectTimetableSlot: (slot: { dayOfWeek: number; startTime: string; endTime: string }) => void;
  onSelectEvent: (event: TeacherTimetableSelectEvent) => void;
  onSelectTimetableDay: (dow: number) => void;
}>;

const StepLessonSchedule = ({
  step1ActiveSection,
  step1UnterrichtCollapsed,
  step1TerminplanCollapsed,
  selectedSubjectName,
  teacherSummaryName,
  selectedCourseId,
  selectedCourse,
  fachOptions,
  fachId,
  teacherOptions,
  teacherId,
  lockTeacher,
  teacherAvailability,
  selectedSlot,
  previewEvent,
  timetableDayOfWeek,
  timetableReferenceDate,
  canShowTerminplan,
  missingScheduleSelectionText,
  teacherAvailabilityHint,
  showOutsideAvailabilityWarning,
  onActivateSection,
  onSelectFach,
  onSelectTeacher,
  onClearCourse,
  onSelectTimetableSlot,
  onSelectEvent,
  onSelectTimetableDay,
}: StepLessonScheduleProps) => {
  const sectionClass = (section: Step1Section) => cn(
    'transition-colors',
    step1ActiveSection === section ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'opacity-90'
  );
  const unterrichtSummary = `${selectedSubjectName} - ${teacherSummaryName}`;

  return (
    <WizardCard className="my-2 flex h-full min-h-0 flex-col">
      <div className={cn('flex h-full min-h-0 flex-col', step1UnterrichtCollapsed ? 'gap-2' : 'gap-4')}>
        {step1UnterrichtCollapsed ? (
          <button
            type="button"
            className={cn(
              'group flex w-full items-center justify-between rounded-xl border bg-muted/20 px-3 py-2 text-left transition-colors',
              sectionClass('unterricht')
            )}
            onClick={() => onActivateSection('unterricht')}
          >
            <span className="truncate text-xs leading-tight text-muted-foreground">
              <span className="font-semibold uppercase tracking-wide">Unterricht</span>{' '}
              <span>{unterrichtSummary}</span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-normal ease-standard motion-safe:group-hover:translate-y-0.5 motion-safe:group-hover:rotate-12 group-hover:text-foreground" />
          </button>
        ) : (
          <WizardSection
            className={sectionClass('unterricht')}
            title={
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-start rounded-md px-0 py-0 text-left normal-case font-normal hover:bg-transparent"
                onClick={() => onActivateSection(step1ActiveSection === 'unterricht' ? 'terminplan' : 'unterricht')}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="uppercase tracking-wide text-muted-foreground">Unterricht</span>
                </span>
              </Button>
            }
          >
            <WizardKVGrid>
                <WizardKVItem label={getSubjectLabels().singular}>
                  <Select value={fachId ? String(fachId) : ''} onValueChange={(v) => onSelectFach(Number(v))}>
                    <SelectTrigger>
                      <SelectValue placeholder={`${getSubjectLabels().singular} auswählen`} />
                    </SelectTrigger>
                    <SelectContent>
                      {fachOptions.map((f) => (
                        <SelectItem key={f.id} value={String(f.id)}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fachId ? null : <div className="mt-1 text-xs text-muted-foreground">Bitte zuerst {getSubjectLabels().singular} auswählen.</div>}
                </WizardKVItem>

                {fachId ? (
                  <WizardKVItem label="Lehrer">
                    <div className="space-y-1">
                      <Select value={teacherId ? String(teacherId) : ''} onValueChange={(v) => onSelectTeacher(Number(v))}>
                        <SelectTrigger disabled={lockTeacher}>
                          <SelectValue placeholder="Lehrer auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {teacherOptions.map((t) => (
                            <SelectItem key={t.id} value={String(t.id)}>
                              {t.vorname} {t.nachname}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {lockTeacher ? (
                        <div className="text-xs text-muted-foreground">
                          Lehrer ist aus dem Terminplan vorgegeben und kann hier nicht geändert werden.
                        </div>
                      ) : null}
                      {!teacherId && fachId ? <div className="text-xs text-muted-foreground">Bitte Lehrer auswählen, um den Terminplan zu sehen.</div> : null}
                    </div>
                  </WizardKVItem>
                ) : null}
              </WizardKVGrid>

              {selectedCourseId && selectedCourse ? (
                <div className="mt-4 rounded-md border bg-blue-50 p-3">
                  <div className="text-sm font-medium">Zugewiesener Kurs</div>
                  <div className="text-sm text-muted-foreground">
                    {selectedCourse.name} ({selectedCourse.dauer_min} Min)
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={onClearCourse}
                  >
                    Entfernen
                  </Button>
                </div>
              ) : null}
          </WizardSection>
        )}

        <WizardSection
          className={cn(sectionClass('terminplan'), !step1TerminplanCollapsed ? 'flex min-h-0 flex-1 flex-col' : '')}
          title={
            <Button
              type="button"
              variant="ghost"
              className="h-auto w-full justify-start rounded-md px-0 py-0 text-left normal-case font-normal hover:bg-transparent"
              onClick={() => onActivateSection(step1ActiveSection === 'terminplan' ? 'unterricht' : 'terminplan')}
            >
              <span className="flex items-center justify-between gap-3">
                <span className="uppercase tracking-wide text-muted-foreground">Terminplan</span>
              </span>
            </Button>
          }
        >
          {step1TerminplanCollapsed ? (
            <Button
              type="button"
              variant="outline"
              className="interactive-lift h-auto w-full justify-start rounded-lg border bg-background/60 p-3 text-left transition-colors hover:bg-muted/30"
              onClick={() => onActivateSection('terminplan')}
            >
              <div className="text-sm text-muted-foreground">Klicken, um den Terminplan zu öffnen.</div>
            </Button>
          ) : null}
          {!step1TerminplanCollapsed && canShowTerminplan ? (
            <div className="flex min-h-[340px] flex-1 flex-col">
              <div className="md:hidden mb-2">
                <div className="flex flex-wrap gap-1">
                  {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((lbl, idx) => {
                    const dow = idx + 1;
                    const active = Number(timetableDayOfWeek) === dow;
                    return (
                      <Button
                        key={lbl}
                        type="button"
                        variant={active ? 'secondary' : 'outline'}
                        size="sm"
                        className={cn('h-7 rounded-md px-2 py-1 text-xs', !active && 'text-muted-foreground hover:bg-muted/50')}
                        onClick={() => onSelectTimetableDay(dow)}
                      >
                        {lbl}
                      </Button>
                    );
                  })}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  In der mobilen Ansicht wechseln Sie hier den Wochentag (kein Datum).
                </div>
              </div>
              <div className="relative min-h-0 flex-1 rounded-lg border">
                <WizardScheduleGrid
                  teacherId={teacherId ?? ''}
                  initialViewMode="week"
                  referenceDate={timetableReferenceDate}
                  availabilitySlots={teacherAvailability}
                  allowOccupiedSelection={true}
                  selectedSlot={
                    selectedSlot
                      ? {
                          dayOfWeek: selectedSlot.dayOfWeek,
                          startTime: selectedSlot.startTime.slice(0, 5),
                        }
                      : null
                  }
                  previewEvent={previewEvent}
                  onSelectSlot={(slot) => {
                    onSelectTimetableSlot({
                      dayOfWeek: slot.dayOfWeek,
                      startTime: slot.startTime,
                      endTime: slot.endTime,
                    });
                  }}
                  onSelectEvent={onSelectEvent}
                />
              </div>
            </div>
          ) : null}
          {!step1TerminplanCollapsed && !canShowTerminplan ? (
            <div className="text-sm text-muted-foreground">{missingScheduleSelectionText}</div>
          ) : null}

          <div className="mt-2 text-xs text-muted-foreground">{teacherAvailabilityHint}</div>

          {showOutsideAvailabilityWarning ? (
            <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900">
              Hinweis: Der ausgewählte Slot liegt außerhalb der Freigabezeiten des Lehrers.
            </div>
          ) : null}
        </WizardSection>
      </div>
    </WizardCard>
  );
};

export default StepLessonSchedule;
